import React, { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  LabelList,
  Bar,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import {
  FieldChoice,
  NewQuota,
  ParticipantFieldChoice,
  ParticipationStatus,
  Quota,
} from '@app/api';
import { useUnmoderatedTabs } from '../../hooks/UnmoderatedTabsConfig';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h5,
    padding: theme.marginBase * 3,
    paddingBottom: 0,
  },
  container: {
    ...theme.fonts.label,
  },
  label: {
    ...theme.fonts.caption,
  },
  black: '#000000',
}));

interface Props {
  title?: string | React.ReactNode;
  choices: Array<FieldChoice | ParticipantFieldChoice>;
  data?: Record<string, Record<string, number>>;
  statusList?: string[];
  quotas?: NewQuota[] | Quota[] | null | undefined;
}
const tickFormatter = (value: string) => {
  const limit = 30;
  if (value.length <= limit) return value;

  const words = value.split(' ');
  const keep = [];
  let len = 0;
  for (const word of words) {
    keep.push(word);
    len += word.length + 1;
    if (len > limit) break;
  }
  return `${keep.join(' ')} ...`;
};
const noData = {} as Record<string, Record<string, number>>;

export const AnswerChart = ({
  title,
  choices,
  data = noData,
  statusList = [],
  quotas = [],
}: Props) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const tabs = useUnmoderatedTabs();

  const allParticipationStatus = useMemo(
    () =>
      statusList.reduce((acc, status) => {
        return acc.concat(tabs[status].statusList);
      }, [] as ParticipationStatus[]),
    [statusList, tabs],
  );
  const showAllAnswers = choices.length < 10;
  const processedQuotas = quotas?.map((q) => ({
    ...q,
    max: 'max' in q ? q.max : q.value,
  }));

  const formatedData = useMemo(() => {
    const result: Array<
      Record<string, number> & { choice: string; quota: number | null }
    > = [];
    if (!choices) {
      return result;
    }

    for (const choice of choices) {
      const label = choice.label;

      const ref = 'ref' in choice ? choice.ref : choice.value;
      if (!data?.[ref] && !showAllAnswers) {
        continue;
      }
      const choiceData = data?.[ref] || {};

      const value = statusList.reduce(
        (acc, status) => {
          const value = tabs[status].statusList.reduce(
            (acc, s) => acc + (choiceData[s] || 0),
            0,
          );
          acc[status] = value;
          return acc;
        },
        {
          choice: label,
          quota:
            'quota' in choice
              ? choice.quota
              : processedQuotas?.find((q) => q.item === ref)?.max ?? null,
        } as Record<string, number> & { choice: string; quota: number | null },
      );

      result.push(value);
    }
    return result;
  }, [showAllAnswers, choices, data, statusList, tabs, allParticipationStatus]);

  if (!formatedData) {
    return null;
  }

  return (
    <div>
      {title &&
        (typeof title === 'string' ? (
          <h4 className={classes.title}>{title}</h4>
        ) : (
          title
        ))}
      <ResponsiveContainer
        height={formatedData.length * 32 + 100}
        className={classes.container}
      >
        <ComposedChart
          width={800}
          height={formatedData.length * 32 + 100}
          layout="vertical"
          data={formatedData}
          margin={{
            top: theme.marginBase * 3,
            right: theme.marginBase * 3,
            bottom: theme.marginBase * 3,
            left: theme.marginBase * 3,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis tick={theme.fonts.caption} type="number" />
          <YAxis
            tick={theme.fonts.calendarText}
            type="category"
            dataKey="choice"
            width={theme.marginBase * 15}
            interval={0}
            tickFormatter={tickFormatter}
          />

          <Tooltip />
          <>
            {statusList.map((status, index) => (
              <Bar
                dataKey={status}
                fill={theme.colorDefinitions[tabs[status].color].std}
                key={status}
                name={tabs[status].label}
                stackId="a"
              >
                {index === statusList.length - 1 && (
                  <LabelList
                    className={classes.label}
                    dataKey="total"
                    position="right"
                    fill={theme.colors.inkSecondary}
                  />
                )}
              </Bar>
            ))}
            <Scatter name="Quota" dataKey="quota" fill={classes.black}>
              <LabelList
                className={classes.label}
                dataKey="quota"
                position="right"
                fill={classes.black}
              />
            </Scatter>
          </>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
